import { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useSSE } from '../hooks/useSSE';
import { useAuth } from '../hooks/useAuth';

/**
 * SSEProvider - Provides SSE functionality to the app
 * 
 * Context value:
 * {
 *   connected: boolean,
 *   subscribeToBooking: (bookingId, callbacks) => unsubscribe function,
 *   onBookingUpdate: (callback) => unsubscribe function,
 *   onBookingCreated: (callback) => unsubscribe function,
 *   onBookingDeleted: (callback) => unsubscribe function,
 * }
 */
const SSEContext = createContext(null);

export const SSEProvider = ({ children }) => {
  const { user } = useAuth();
  const globalSubscribersRef = useRef({
    bookingUpdate: [],
    bookingCreated: [],
    bookingDeleted: [],
  });
  const bookingSubscribersRef = useRef({});

  // Handle booking update events
  const handleBookingUpdate = useCallback((event) => {
    // Notify global subscribers
    globalSubscribersRef.current.bookingUpdate.forEach((callback) => {
      try {
        callback(event);
      } catch (err) {
        console.error('Error in booking update callback:', err);
      }
    });

    // Notify booking-specific subscribers
    const bookingId = event.bookingId || event.data?._id;
    if (bookingId && bookingSubscribersRef.current[bookingId]) {
      const subscribers = bookingSubscribersRef.current[bookingId];
      subscribers.forEach((subscriber) => {
        if (subscriber.onBookingUpdate) {
          try {
            subscriber.onBookingUpdate(event);
          } catch (err) {
            console.error('Error in booking-specific update callback:', err);
          }
        }
      });
    }
  }, []);

  // Handle booking created events
  const handleBookingCreated = useCallback((event) => {
    globalSubscribersRef.current.bookingCreated.forEach((callback) => {
      try {
        callback(event);
      } catch (err) {
        console.error('Error in booking created callback:', err);
      }
    });
  }, []);

  // Handle booking deleted events
  const handleBookingDeleted = useCallback((event) => {
    globalSubscribersRef.current.bookingDeleted.forEach((callback) => {
      try {
        callback(event);
      } catch (err) {
        console.error('Error in booking deleted callback:', err);
      }
    });

    // Notify booking-specific subscribers
    const bookingId = event.bookingId;
    if (bookingId && bookingSubscribersRef.current[bookingId]) {
      const subscribers = bookingSubscribersRef.current[bookingId];
      subscribers.forEach((subscriber) => {
        if (subscriber.onBookingDeleted) {
          try {
            subscriber.onBookingDeleted(event);
          } catch (err) {
            console.error('Error in booking-specific deleted callback:', err);
          }
        }
      });
    }
  }, []);

  // Handle activity log events
  const handleActivityLog = useCallback((event) => {
    const bookingId = event.bookingId || event.data?.bookingId;
    if (bookingId && bookingSubscribersRef.current[bookingId]) {
      const subscribers = bookingSubscribersRef.current[bookingId];
      subscribers.forEach((subscriber) => {
        if (subscriber.onActivityLog) {
          try {
            subscriber.onActivityLog(event);
          } catch (err) {
            console.error('Error in activity log callback:', err);
          }
        }
      });
    }
  }, []);

  // Establish SSE connection (only when user is authenticated)
  const { connected, error } = useSSE('/sse/bookings', {
    enabled: !!user,
    onBookingUpdate: handleBookingUpdate,
    onBookingCreated: handleBookingCreated,
    onBookingDeleted: handleBookingDeleted,
    onActivityLog: handleActivityLog,
  });

  // Subscribe to global booking updates
  const onBookingUpdate = useCallback((callback) => {
    globalSubscribersRef.current.bookingUpdate.push(callback);
    return () => {
      globalSubscribersRef.current.bookingUpdate =
        globalSubscribersRef.current.bookingUpdate.filter((cb) => cb !== callback);
    };
  }, []);

  // Subscribe to global booking created events
  const onBookingCreated = useCallback((callback) => {
    globalSubscribersRef.current.bookingCreated.push(callback);
    return () => {
      globalSubscribersRef.current.bookingCreated =
        globalSubscribersRef.current.bookingCreated.filter((cb) => cb !== callback);
    };
  }, []);

  // Subscribe to global booking deleted events
  const onBookingDeleted = useCallback((callback) => {
    globalSubscribersRef.current.bookingDeleted.push(callback);
    return () => {
      globalSubscribersRef.current.bookingDeleted =
        globalSubscribersRef.current.bookingDeleted.filter((cb) => cb !== callback);
    };
  }, []);

  // Subscribe to specific booking updates
  const subscribeToBooking = useCallback((bookingId, callbacks) => {
    if (!bookingId) {
      console.warn('subscribeToBooking called without bookingId');
      return () => {};
    }

    if (!bookingSubscribersRef.current[bookingId]) {
      bookingSubscribersRef.current[bookingId] = [];
    }

    const subscriber = {
      onBookingUpdate: callbacks.onBookingUpdate,
      onBookingDeleted: callbacks.onBookingDeleted,
      onActivityLog: callbacks.onActivityLog,
    };

    bookingSubscribersRef.current[bookingId].push(subscriber);

    // Return unsubscribe function
    return () => {
      if (bookingSubscribersRef.current[bookingId]) {
        bookingSubscribersRef.current[bookingId] =
          bookingSubscribersRef.current[bookingId].filter((sub) => sub !== subscriber);
        
        // Clean up empty arrays
        if (bookingSubscribersRef.current[bookingId].length === 0) {
          delete bookingSubscribersRef.current[bookingId];
        }
      }
    };
  }, []);

  // Log connection errors
  useEffect(() => {
    if (error) {
      console.warn('SSE connection error:', error);
    }
  }, [error]);

  const value = {
    connected,
    subscribeToBooking,
    onBookingUpdate,
    onBookingCreated,
    onBookingDeleted,
  };

  return <SSEContext.Provider value={value}>{children}</SSEContext.Provider>;
};

export const useSSEContext = () => {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error('useSSEContext must be used within SSEProvider');
  }
  return context;
};
